import {
  ScheduleClosingRequirementsModel,
  ScheduleSettingsModel,
} from '@user/store/closingRequirements/typings'
import { useAppDispatch } from '@user/store/hooks'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetScheduleRequirementsOnMount } from '../../hooks/useGetScheduleRequirementsOnMount'
import { ScheduleRequirement } from './ScheduleRequirement'
import { updateScheduleRequirementSettings } from '@user/store/closingRequirements/api'
import { toast } from '@sales-center/components'
import SchedulingIcon from '../../atoms/Icon/SchedulingIcon'
import { RequirementsHeader } from '../RequirementsHeader'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Divider from '@mui/material/Divider'
import { Theme } from '@mui/material/styles'
import { InfoIcon } from '../../atoms/Icon/InfoIcon'
import { ClosingRequirementsForm } from '@user/components/Settings/Home/ClosingRequirementsForm'

interface Props {
  onClose: () => void
}

export const SchedulesRequirementForm: React.FC<Props> = (props: Props) => {
  const { onClose } = props
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()

  const [requirements, setRequirements] = useState<ScheduleSettingsModel>()

  const { scheduleRequirementsStatus, scheduleRequirements } =
    useGetScheduleRequirementsOnMount()

  useEffect(() => {
    if (scheduleRequirementsStatus === 'success' && scheduleRequirements) {
      setRequirements(scheduleRequirements)
    }
  }, [scheduleRequirementsStatus, scheduleRequirements])

  const anyEventsSelected = useMemo(() => {
    return (
      requirements?.scheduleVisibilitySettings.freeEstimateScheduleEnabled ||
      requirements?.scheduleVisibilitySettings.paidEstimateScheduleEnabled ||
      requirements?.scheduleVisibilitySettings.serviceScheduleEnabled
    )
  }, [
    requirements?.scheduleVisibilitySettings.freeEstimateScheduleEnabled,
    requirements?.scheduleVisibilitySettings.paidEstimateScheduleEnabled,
    requirements?.scheduleVisibilitySettings.serviceScheduleEnabled,
  ])

  const handleSubmit = useCallback(async () => {
    if (requirements) {
      setIsLoading(true)
      const result = await dispatch(
        updateScheduleRequirementSettings(requirements),
      )

      if (updateScheduleRequirementSettings.fulfilled.match(result)) {
        setIsLoading(false)
        toast.success('Scheduling Closing Requirements have been updated.')
        onClose()
      } else {
        setIsLoading(false)
        toast.error(
          'We are unable to update Scheduling Closing Requirements at this time.',
        )
      }
      setTimeout(() => {
        onClose()
      }, 0)
    }
  }, [dispatch, onClose, requirements])

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setRequirements((current) => {
        if (!current) return current

        const { scheduleClosingRequirements } = current

        const name = e.target?.name ?? e.currentTarget?.name

        const updatedRequirement = scheduleClosingRequirements.map((req) => {
          if (name === 'freeEstimateScheduleEnabled') {
            return { ...req, requireFreeEstimateSchedule: false }
          } else if (name === 'paidEstimateScheduleEnabled')
            return { ...req, requirePaidEstimateSchedule: false }
          else if (name === 'serviceScheduleEnabled')
            return { ...req, requireServiceSchedule: false }
          else return req
        })

        return {
          ...current,
          scheduleClosingRequirements: updatedRequirement,
          scheduleVisibilitySettings: {
            ...current.scheduleVisibilitySettings,
            [name]: checked,
          },
        }
      })
    },
    [],
  )

  const onSelected = useCallback((model: ScheduleClosingRequirementsModel) => {
    setRequirements((current) => {
      if (!current) return
      const items = current.scheduleClosingRequirements.map((x) => {
        if (x.salesFunnelId === model.salesFunnelId) return model
        else return x
      })
      return {
        ...current,
        scheduleClosingRequirements: items,
      }
    })
  }, [])

  return (
    <ClosingRequirementsForm
      onSave={handleSubmit}
      onCancel={onClose}
      secondaryBtnLabel="Cancel"
      disableSave={isLoading}
      padding="0 20px"
      primaryBtnLabel="Save"
    >
      {scheduleRequirementsStatus === 'pending' && (
        <Box
          sx={{
            paddingTop: (theme: Theme) => theme.spacing(4.8),
            textAlign: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {scheduleRequirementsStatus === 'fail' && (
        <Box
          sx={{
            paddingTop: (theme: Theme) => theme.spacing(4.8),
            textAlign: 'center',
          }}
        >
          <Alert severity="error">Failed to load Closing Requirements.</Alert>
        </Box>
      )}

      {scheduleRequirementsStatus === 'success' && (
        <RequirementsHeader
          title="Closing Requirements for Scheduling"
          subTitle="Turn on the event type(s) you want to schedule and select which
              events must be completed to close/win sales."
          icon={<SchedulingIcon></SchedulingIcon>}
        ></RequirementsHeader>
      )}

      {!!requirements && requirements.scheduleVisibilitySettings && (
        <div>
          <Box display="flex" alignItems="center" mt="10px">
            <Typography variant="h6">Scheduling Event Types</Typography>
          </Box>{' '}
          <Box display="flex" alignItems="center">
            <Typography variant="body2">
              Select schedule types to hide/show in the closing requirements.
              This setting is global and applies to all sales funnels.
            </Typography>
          </Box>
          <Box mt="25px">
            <FormControlLabel
              label="Free Estimate"
              control={
                <Switch
                  name="freeEstimateScheduleEnabled"
                  checked={
                    requirements?.scheduleVisibilitySettings
                      .freeEstimateScheduleEnabled
                  }
                  onChange={onChange}
                />
              }
            />
          </Box>
          <Box>
            <FormControlLabel
              label="Paid Estimate"
              control={
                <Switch
                  name="paidEstimateScheduleEnabled"
                  checked={
                    requirements?.scheduleVisibilitySettings
                      .paidEstimateScheduleEnabled
                  }
                  onChange={onChange}
                />
              }
            />
          </Box>
          <Box>
            <FormControlLabel
              label="Service"
              control={
                <Switch
                  name="serviceScheduleEnabled"
                  checked={
                    requirements?.scheduleVisibilitySettings
                      .serviceScheduleEnabled
                  }
                  onChange={onChange}
                />
              }
            />
          </Box>
          <Divider sx={{ mt: '28px' }} />
          <Box display="flex" alignItems="center" mt={2}>
            <Typography variant="h6">
              Closing Requirements for Scheduling
            </Typography>
          </Box>{' '}
          <Box display="flex" alignItems="center">
            <Typography variant="body2">
              Select below which sales funnels require scheduling to close/win.
            </Typography>
          </Box>
          {!anyEventsSelected && (
            <Box display="block" alignItems="center" mt={2.0}>
              <Alert severity="info" icon={<InfoIcon></InfoIcon>}>
                No scheduling event types selected
              </Alert>
            </Box>
          )}
        </div>
      )}

      {!!requirements &&
        requirements.scheduleClosingRequirements.map((x) => (
          <ScheduleRequirement
            {...x}
            onSelected={onSelected}
            key={x.salesFunnelId}
            visibilitySettings={requirements.scheduleVisibilitySettings}
          ></ScheduleRequirement>
        ))}
    </ClosingRequirementsForm>
  )
}
